import { useState, useEffect } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, TrendingUp, Ticket, Gift, Star, Award } from "lucide-react";
import { toast } from "sonner";

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get("/admin/analytics");
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Analytics Dashboard">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="h-32 rounded-xl animate-shimmer" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Analytics Dashboard">
      <div className="space-y-6 animate-fade-in">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <Users className="w-6 h-6 text-[#D4AF37] mb-3" />
            <p className="stat-value">{analytics?.members?.total?.toLocaleString() || 0}</p>
            <p className="stat-label">Total Members</p>
          </div>
          <div className="stat-card">
            <TrendingUp className="w-6 h-6 text-[#4ADE80] mb-3" />
            <p className="stat-value">{analytics?.members?.active?.toLocaleString() || 0}</p>
            <p className="stat-label">Active Members</p>
          </div>
          <div className="stat-card">
            <Star className="w-6 h-6 text-[#60A5FA] mb-3" />
            <p className="stat-value">{analytics?.points?.issued?.toLocaleString() || 0}</p>
            <p className="stat-label">Miles Issued</p>
          </div>
          <div className="stat-card">
            <Gift className="w-6 h-6 text-[#F59E0B] mb-3" />
            <p className="stat-value">{analytics?.points?.redeemed?.toLocaleString() || 0}</p>
            <p className="stat-label">Miles Redeemed</p>
          </div>
        </div>

        {/* Voucher Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Total Vouchers</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {analytics?.vouchers?.total || 0}
                  </p>
                </div>
                <Ticket className="w-10 h-10 text-[#D4AF37] opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Active Vouchers</p>
                  <p className="text-3xl font-bold text-[#4ADE80] mt-1">
                    {analytics?.vouchers?.active || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#4ADE80]/10 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-[#4ADE80]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A0A0A0] text-sm">Used Vouchers</p>
                  <p className="text-3xl font-bold text-[#D4AF37] mt-1">
                    {analytics?.vouchers?.used || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-[#D4AF37]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tier Distribution */}
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardHeader>
              <CardTitle className="text-white">Tier Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(analytics?.tier_distribution || {}).map(([tier, count]) => {
                  const total = Object.values(analytics?.tier_distribution || {}).reduce((a, b) => a + b, 0);
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                  const color = tier === 'Gold' ? '#D4AF37' : tier === 'Platinum' ? '#E5E4E2' : '#C0C0C0';
                  
                  return (
                    <div key={tier}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white font-medium">{tier}</span>
                        <span className="text-[#A0A0A0]">{count} ({percent}%)</span>
                      </div>
                      <div className="h-2 bg-[#1A1A1E] rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${percent}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(analytics?.tier_distribution || {}).length === 0 && (
                  <p className="text-[#6B6B6B] text-center py-4">No tier data available</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Customers */}
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardHeader>
              <CardTitle className="text-white">Top Customers</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.top_customers?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_customers.slice(0, 5).map((customer, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-[#0A0A0B] rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        idx === 0 ? 'bg-[#D4AF37] text-[#0A0A0B]' :
                        idx === 1 ? 'bg-[#C0C0C0] text-[#0A0A0B]' :
                        idx === 2 ? 'bg-[#CD7F32] text-[#0A0A0B]' :
                        'bg-[#1A1A1E] text-[#A0A0A0]'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{customer.name}</p>
                        <p className="text-[#6B6B6B] text-xs truncate">{customer.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] font-semibold">
                          {customer.lifetime_points?.toLocaleString()}
                        </p>
                        <p className="text-[#6B6B6B] text-xs">miles</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#6B6B6B] text-center py-8">No customer data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Redemptions by Type */}
        {Object.keys(analytics?.redemptions_by_type || {}).length > 0 && (
          <Card className="bg-[#131316] border-[#2A2A2F]">
            <CardHeader>
              <CardTitle className="text-white">Redemptions by Reward</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(analytics.redemptions_by_type).map(([name, count]) => (
                  <div key={name} className="bg-[#0A0A0B] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-[#D4AF37]">{count}</p>
                    <p className="text-[#A0A0A0] text-sm mt-1 truncate">{name}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#A0A0A0] text-sm">Rides in Last 30 Days</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {analytics?.recent_rides_30d || 0}
                </p>
              </div>
              <Award className="w-12 h-12 text-[#D4AF37] opacity-30" />
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
